import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Box, Typography, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import AppButton from "../../../common_components/AppButton";
import { TextInput, Dropdown } from "../../../common_components/FormInputs";
import { toast } from "../../../common_components/Toast/ToastUtils";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useDispatch } from "react-redux";
import { createManualAdRequest, updateManualAdRequest } from "../../../redux/clientAdApprovalSlice";
const TEST_GOAL_OPTIONS = [
    { value: "Copy Test", label: "Copy Test" },
    { value: "New Test", label: "New Test" },
    { value: "Creative Test", label: "Creative Test" },
    { value: "Landing Page Test", label: "Landing Page Test" },
];
const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["bold", "italic", "underline"],
        ["link", "image"],
        ["code-block", "blockquote"],
    ],
};
const QUILL_FORMATS = [
    "header",
    "list",
    "bullet",
    "bold",
    "italic",
    "underline",
    "link",
    "image",
    "code-block",
    "blockquote",
];
const CreateAdRequestDrawer = ({ open, onClose, connectionId, editingAdRequest, onSuccess, onSubmittingChange, }) => {
    const isEditMode = Boolean(editingAdRequest && editingAdRequest._id);
    const dispatch = useDispatch();
    const [adRequestName, setAdRequestName] = useState("");
    const [testGoal, setTestGoal] = useState("Copy Test");
    const [testSummary, setTestSummary] = useState("");
    const [fileError, setFileError] = useState(null);
    const [files, setFiles] = useState([]);
    const [existingFiles, setExistingFiles] = useState([]);
    const [removedExistingFileNames, setRemovedExistingFileNames] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [fileProgress, setFileProgress] = useState({});
    const [fileUploading, setFileUploading] = useState({});
    const [deleteModal, setDeleteModal] = useState({ open: false, key: null, file: null });
    const fileRef = useRef(null);
    const lastHydratedSummaryRef = useRef("");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewName, setPreviewName] = useState("");
    const [previewMime, setPreviewMime] = useState("");
    const fileKey = (f) => `${f.name}_${f.size}_${f.lastModified}`;
    const formatBytes = (bytes) => {
        if (!Number.isFinite(bytes) || bytes <= 0)
            return "0 B";
        const mb = bytes / (1024 * 1024);
        if (mb >= 1)
            return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
        const kb = bytes / 1024;
        return `${kb.toFixed(kb >= 10 ? 0 : 1)} KB`;
    };
    const formatDate = (d) => d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    const getFileTypeLabel = (file) => {
        const ext = (file.name.split(".").pop() || "").toUpperCase();
        if (ext)
            return ext;
        const mime = (file.type || "").toLowerCase();
        if (mime.includes("jpeg"))
            return "JPG";
        if (mime.includes("png"))
            return "PNG";
        if (mime.includes("webp"))
            return "WEBP";
        if (mime.includes("pdf"))
            return "PDF";
        return "FILE";
    };
    const existingFileUrl = (f) => {
        const u = (f?.url || f?.file_url || f?.data || f?.path || "").trim();
        return u.length ? u : null;
    };
    const existingFileKey = (f) => {
        const url = existingFileUrl(f) || "";
        const name = String(f?.file_name || f?.name || "").trim();
        const size = String(typeof f?.size_bytes === "number"
            ? f.size_bytes
            : (typeof f?.size === "number" ? f.size : ""));
        // Prefer URL (unique for data URLs), otherwise fall back to name+size
        const stableId = url || `${name}:${size}`;
        return `existing:${stableId}`;
    };
    const existingFileLabel = (f, index) => (f?.name || f?.file_name || `Attachment ${index + 1}`).trim();
    const existingFileSize = (f) => typeof f?.size === "number"
        ? f.size
        : (typeof f?.size_bytes === "number" ? f.size_bytes : 0);
    const existingFileDate = (f) => {
        const raw = f?.uploaded_at || f?.uploadedAt || f?.createdAt;
        const dt = raw ? new Date(raw) : null;
        return dt && !Number.isNaN(dt.getTime()) ? dt : null;
    };
    const existingFileTypeLabel = (f) => {
        const name = (f?.name || f?.file_name || "").trim();
        const ext = (name.split(".").pop() || "").toUpperCase();
        if (ext)
            return ext;
        const mime = (f?.mimetype || f?.mime_type || "").toLowerCase();
        if (mime.includes("jpeg"))
            return "JPG";
        if (mime.includes("png"))
            return "PNG";
        if (mime.includes("webp"))
            return "WEBP";
        if (mime.includes("pdf"))
            return "PDF";
        return "FILE";
    };
    useEffect(() => {
        if (!open)
            return;
        // EDIT
        if (editingAdRequest?._id) {
            setAdRequestName(editingAdRequest.ad_name ?? "");
            setTestGoal(editingAdRequest.test_goal ?? "Copy Test");
            const summary = String(editingAdRequest.test_summary ?? "");
            lastHydratedSummaryRef.current = summary;
            setTestSummary(summary);
            // Backend field name is `upload_files`
            const apiFiles = editingAdRequest.upload_files ?? [];
            setExistingFiles(Array.isArray(apiFiles) ? apiFiles : []);
            setRemovedExistingFileNames([]);
            // New file selection starts empty in edit mode
            setFiles([]);
            setFileProgress({});
            setFileUploading({});
            setFileError(null);
            return;
        }
        // CREATE
        setAdRequestName("");
        setTestGoal("Copy Test");
        lastHydratedSummaryRef.current = "";
        setTestSummary("");
        setFiles([]);
        setExistingFiles([]);
        setRemovedExistingFileNames([]);
        setFileProgress({});
        setFileUploading({});
        setFileError(null);
    }, [open, editingAdRequest?._id]);
    useEffect(() => {
        if (!files.length)
            return;
        const hasAnyUploading = isSaving || Object.values(fileUploading).some((v) => v === true);
        if (!hasAnyUploading)
            return;
        const timer = window.setInterval(() => {
            setFileProgress((prev) => {
                const next = { ...prev };
                for (const f of files) {
                    const key = fileKey(f);
                    const cur = typeof next[key] === "number" ? next[key] : 0;
                    if (!isSaving && fileUploading[key] !== true) {
                        // Not currently uploading this file; keep progress as-is.
                        continue;
                    }
                    const step = Math.max(3, Math.round((100 - cur) * 0.2));
                    next[key] = Math.min(cur + step, 95);
                }
                return next;
            });
            // Mark completed uploads as done (selection upload simulation)
            setFileUploading((prev) => {
                if (isSaving)
                    return prev;
                let changed = false;
                const next = { ...prev };
                for (const f of files) {
                    const key = fileKey(f);
                    if (next[key] === true) {
                        const p = fileProgress[key] ?? 0;
                        if (p >= 95) {
                            next[key] = false;
                            changed = true;
                        }
                    }
                }
                return changed ? next : prev;
            });
        }, 300);
        return () => window.clearInterval(timer);
    }, [isSaving, files, fileUploading, fileProgress]);
    const openFilePicker = () => {
        fileRef.current?.click();
    };
    const handleFileUpload = async (e) => {
        const uploadedFiles = Array.from(e.target.files || []);
        setFileError(null);
        const validFiles = [];
        for (let i = 0; i < uploadedFiles.length; i++) {
            const file = uploadedFiles[i];
            if (file.size > 5 * 1024 * 1024) {
                setFileError("Some files were skipped (max 5MB each).");
                continue;
            }
            validFiles.push(file);
        }
        if (validFiles.length === 0) {
            if (fileRef.current)
                fileRef.current.value = "";
            return;
        }
        setFiles((prev) => {
            const existing = Array.isArray(prev) ? prev : [];
            const existingKeys = new Set(existing.map((f) => fileKey(f)));
            const toAdd = validFiles.filter((f) => !existingKeys.has(fileKey(f)));
            return [...existing, ...toAdd];
        });
        setFileProgress((prev) => {
            const next = { ...(prev || {}) };
            for (const f of validFiles) {
                // if this exact file already exists, don't reset its progress
                const key = fileKey(f);
                if (next[key] === undefined)
                    next[key] = 0;
            }
            return next;
        });
        setFileUploading((prev) => {
            const next = { ...(prev || {}) };
            for (const f of validFiles) {
                next[fileKey(f)] = true;
            }
            return next;
        });
        if (fileRef.current)
            fileRef.current.value = "";
    };
    const removeFile = (key) => {
        setFiles((prev) => prev.filter((f) => fileKey(f) !== key));
        setFileProgress((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setFileUploading((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };
    const previewFile = (file) => {
        const url = URL.createObjectURL(file);
        window.open(url, "_blank", "noopener,noreferrer");
        window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    };
    const previewExistingFile = (f) => {
        const url = existingFileUrl(f);
        if (!url) {
            toast.error("Preview not available for this file.");
            return;
        }
        setPreviewUrl(url);
        setPreviewName(String(f?.file_name || f?.name || "Attachment").trim());
        setPreviewMime(String(f?.mime_type || f?.mimetype || "").trim());
        setPreviewOpen(true);
    };
    const handleRemoveExistingFile = () => {
        if (!deleteModal.key || !deleteModal.file)
            return;
        const key = deleteModal.key;
        const f = deleteModal.file;
        const name = String(f?.file_name || f?.name || "").trim();
        if (name) {
            setRemovedExistingFileNames((prev) => (prev.includes(name) ? prev : [...prev, name]));
        }
        setExistingFiles((prev) => (Array.isArray(prev) ? prev : []).filter((file) => existingFileKey(file) !== key));
        setDeleteModal({ open: false, key: null, file: null });
    };
    const handleSave = async () => {
        try {
            if (!adRequestName.trim()) {
                toast.error("Ad Request Name is required");
                return;
            }
            if (!connectionId && !isEditMode) {
                toast.error("No Meta account is connected to this store. Please connect a Meta account before creating an Ad Request.");
                return;
            }
            setIsSaving(true);
            if (onSubmittingChange)
                onSubmittingChange(true);
            setFileProgress((prev) => {
                const next = { ...prev };
                for (const f of files)
                    next[fileKey(f)] = 0;
                return next;
            });
            const formData = new FormData();
            if (!isEditMode) {
                formData.append("connectionId", connectionId);
            }
            formData.append("ad_request_name", adRequestName.trim());
            formData.append("test_goal", testGoal);
            formData.append("test_summary", testSummary);
            if (isEditMode && removedExistingFileNames.length > 0) {
                formData.append("remove_upload_files", JSON.stringify(removedExistingFileNames));
            }
            files.forEach((file) => {
                // include filename explicitly to help backend + debugging
                formData.append("files", file, file.name);
            });
            if (isEditMode) {
                await dispatch(updateManualAdRequest({ id: editingAdRequest._id, formData })).unwrap();
            }
            else {
                await dispatch(createManualAdRequest({ formData })).unwrap();
            }
            setFileProgress((prev) => {
                const next = { ...prev };
                for (const f of files)
                    next[fileKey(f)] = 100;
                return next;
            });
            setFileUploading((prev) => {
                const next = { ...prev };
                for (const f of files)
                    next[fileKey(f)] = false;
                return next;
            });
            onSuccess();
            onClose();
        }
        catch (error) {
            // Error toasts are already handled inside thunks; keep a generic fallback here.
            if (error?.message) {
                toast.error(error.message);
            }
            else {
                toast.error("Failed to save ad request. Please try again.");
            }
        }
        finally {
            setIsSaving(false);
            if (onSubmittingChange)
                onSubmittingChange(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: isEditMode ? "Edit Ad Request" : "Create New Ad Request", headerActions: _jsx(AppButton, { variant: "contained", color: "primary", className: "app-common-buttonx", onClick: handleSave, disabled: isSaving, children: isEditMode ? "Save Changes" : "Save Request" }), width: "md", children: _jsxs(Box, { sx: { p: 1, display: "flex", flexDirection: "column", gap: 1 }, children: [_jsxs(Box, { sx: {
                                borderRadius: "8px",
                                border: "1px solid #E5E7EB",
                                backgroundColor: "#FFFFFF",
                                p: 1.5,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }, children: [_jsx("h3", { className: "all-Font-Sizes ", style: { color: "#FF008E", margin: 0, borderBottom: "1px solid #EAECF0", paddingBottom: 8 }, children: " Ad Information" }), _jsxs(Box, { sx: { display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 2 }, children: [_jsx(TextInput, { label: "Ad Request Name", required: true, placeholder: "Ad Request Name", value: adRequestName, onChange: (e) => setAdRequestName(e.target.value) }), _jsx(Dropdown, { label: "Test Goal", value: testGoal, options: TEST_GOAL_OPTIONS, onChange: (val) => setTestGoal(String(val)), placeholder: "Select goal", usePortal: true })] }), _jsxs(Box, { children: [_jsx("h3", { className: "all-Font-Sizes ", style: { color: "#FF008E", margin: 0, borderBottom: "1px solid #EAECF0", paddingBottom: 8 }, children: " Test Summary" }), _jsx(Box, { sx: {
                                                borderRadius: "8px",
                                                border: "1px solid #E5E7EB",
                                                overflow: "hidden",
                                                mt: 2,
                                                backgroundColor: "#FFFFFF",
                                                "& .ql-toolbar": {
                                                    border: "none",
                                                    borderBottom: "1px solid #E5E7EB",
                                                    backgroundColor: "#F9FAFB",
                                                },
                                                "& .ql-container": {
                                                    border: "none",
                                                },
                                                "& .ql-editor": {
                                                    minHeight: 160,
                                                    fontSize: 14,
                                                },
                                            }, children: _jsx(ReactQuill, { theme: "snow", value: testSummary, onChange: (val, _delta, source) => {
                                                    // Quill can emit an "api" change during mount that may clobber the fetched HTML.
                                                    // Only persist actual user edits to state.
                                                    if (source !== "user")
                                                        return;
                                                    setTestSummary(String(val ?? ""));
                                                }, placeholder: "Type your text here...", modules: QUILL_MODULES, formats: QUILL_FORMATS }, `${open ? "open" : "closed"}:${isEditMode ? `edit:${editingAdRequest?._id ?? ""}` : "create"}`) })] }), _jsx(Typography, { sx: {
                                        fontSize: 14,
                                        color: "#B54708",
                                        backgroundColor: "#FFFAEB",
                                        padding: 1,
                                        textAlign: "center",
                                        borderRadius: "8px",
                                    }, children: "*To upload the video, please use Google Drive and add a Drive link to the description area." })] }), _jsxs(Box, { sx: {
                                borderRadius: "8px",
                                border: "1px solid #E5E7EB",
                                backgroundColor: "#FFFFFF",
                                p: 2.5,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }, children: [_jsx(Typography, { sx: { fontSize: 14, fontWeight: 600, color: "#4B5563" }, children: "Upload File" }), _jsxs(Box, { sx: {
                                        border: "1px dashed #D1D5DB",
                                        borderRadius: 1,
                                        padding: "28px",
                                        textAlign: "center",
                                        background: files.length > 0 ? "#F9FAFB" : "#fff",
                                        cursor: files.length > 0 ? "not-allowed" : "pointer",
                                        opacity: files.length > 0 ? 0.7 : 1,
                                        pointerEvents: files.length > 0 ? "none" : "auto",
                                    }, onClick: files.length === 0 ? openFilePicker : undefined, children: [_jsx("input", { ref: fileRef, type: "file", multiple: true, style: { display: "none" }, accept: "image/png,image/jpeg,image/webp,application/pdf", onChange: handleFileUpload, disabled: files.length > 0 }), _jsx(Box, { sx: {
                                                width: 40,
                                                height: 40,
                                                borderRadius: "50%",
                                                background: "#F3F4F6",
                                                margin: "0 auto 10px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                filter: files.length > 0 ? "grayscale(100%)" : "none"
                                            }, children: _jsx("img", { src: "/assets/upload.svg", alt: "" }) }), _jsx(Typography, { children: files.length > 0 ? "File Selected" : (_jsxs(_Fragment, { children: ["Drag and Drop or", " ", _jsx("span", { style: { color: "#FF008E", fontWeight: 500 }, children: "Browse" }), " ", "to upload"] })) }), _jsx(Typography, { sx: { fontSize: 12, color: "#6B7280" }, children: files.length > 0 ? "Check the file list below" : "Supported Format: PNG, JPEG, WebP" })] }), fileError && (_jsxs(Typography, { sx: { color: "red", fontSize: 13, mt: 1, fontWeight: 500 }, children: ["* ", fileError] })), Array.isArray(existingFiles) && existingFiles.length > 0 && (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: existingFiles.map((f, idx) => {
                                        const key = existingFileKey(f);
                                        const dt = existingFileDate(f);
                                        const size = existingFileSize(f);
                                        return (_jsxs(Box, { sx: {
                                                borderRadius: "8px",
                                                border: "1px solid #E5E7EB",
                                                backgroundColor: "#FFFFFF",
                                                p: 1.5,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.75,
                                            }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 }, children: [_jsx(Box, { sx: {
                                                                        width: 32,
                                                                        height: 32,
                                                                        borderRadius: "8px",
                                                                        border: "1px solid #E5E7EB",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        backgroundColor: "#FFFFFF",
                                                                        fontSize: 10,
                                                                        fontWeight: 600,
                                                                        color: "#6B7280",
                                                                    }, children: existingFileTypeLabel(f) }), _jsxs(Box, { sx: { display: "flex", flexDirection: "column" }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 500, color: "#111827" }, children: existingFileLabel(f, idx) }), _jsxs(Typography, { sx: { fontSize: 11, color: "#6B7280" }, children: ["Date: ", dt ? formatDate(dt) : "-", size ? _jsxs(_Fragment, { children: [" \u00A0 Size: ", formatBytes(size)] }) : null] })] })] }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, color: "#6B7280", fontSize: 12 }, children: [_jsx(Box, { component: "button", type: "button", onClick: () => setDeleteModal({ open: true, key, file: f }), disabled: isSaving, sx: {
                                                                        width: 18,
                                                                        height: 18,
                                                                        border: "none",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: 12,
                                                                        background: "#FFFFFF",
                                                                        cursor: isSaving ? "not-allowed" : "pointer",
                                                                        padding: 0,
                                                                    }, "aria-label": "Delete file", children: _jsx("img", { src: "/assets/delete.svg", alt: "" }) }), _jsx(Box, { component: "button", type: "button", onClick: () => previewExistingFile(f), sx: {
                                                                        width: 18,
                                                                        height: 18,
                                                                        border: "none",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: 12,
                                                                        background: "#FFFFFF",
                                                                        cursor: "pointer",
                                                                        padding: 0,
                                                                    }, "aria-label": "Preview file", children: _jsx("img", { src: "/assets/eye.svg", alt: "" }) })] })] }), _jsx(Typography, { sx: { fontSize: 11, fontWeight: 500, color: "#16A34A", ml: 5.5 }, children: "Uploaded" })] }, key));
                                    }) })), Array.isArray(files) && files.length > 0 && (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: files.map((file) => {
                                        const key = fileKey(file);
                                        const progress = Math.max(0, Math.min(100, fileProgress[key] ?? 100));
                                        const isUploading = (isSaving && progress < 100) || fileUploading[key] === true;
                                        return (_jsxs(Box, { sx: {
                                                borderRadius: "8px",
                                                border: isUploading ? "1px solid #F472B6" : "1px solid #E5E7EB",
                                                backgroundColor: isUploading ? "#FFF7FB" : "#FFFFFF",
                                                p: 1.5,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 0.75,
                                            }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5 }, children: [_jsx(Box, { sx: {
                                                                        width: 32,
                                                                        height: 32,
                                                                        borderRadius: "8px",
                                                                        border: "1px solid #E5E7EB",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        backgroundColor: "#FFFFFF",
                                                                        fontSize: 10,
                                                                        fontWeight: 600,
                                                                        color: "#6B7280",
                                                                    }, children: getFileTypeLabel(file) }), _jsxs(Box, { sx: { display: "flex", flexDirection: "column" }, children: [_jsx(Typography, { sx: { fontSize: 13, fontWeight: 500, color: "#111827" }, children: file.name }), _jsxs(Typography, { sx: { fontSize: 11, color: "#6B7280" }, children: ["Date: ", formatDate(new Date(file.lastModified || Date.now())), "\u00A0 Size: ", formatBytes(file.size)] })] })] }), isUploading ? (_jsx(Box, { component: "button", type: "button", onClick: () => removeFile(key), disabled: isSaving, sx: {
                                                                border: "none",
                                                                background: "transparent",
                                                                color: "#6B7280",
                                                                cursor: isSaving ? "not-allowed" : "pointer",
                                                                fontSize: 18,
                                                                lineHeight: 1,
                                                                padding: 0,
                                                            }, "aria-label": "Remove file", children: "\u00D7" })) : (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, color: "#6B7280", fontSize: 12 }, children: [_jsx(Box, { component: "button", type: "button", onClick: () => removeFile(key), disabled: isSaving, sx: {
                                                                        width: 18,
                                                                        height: 18,
                                                                        border: "none",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: 12,
                                                                        background: "#FFFFFF",
                                                                        cursor: isSaving ? "not-allowed" : "pointer",
                                                                        padding: 0,
                                                                    }, "aria-label": "Delete file", children: _jsx("img", { src: "/assets/delete.svg", alt: "" }) }), _jsx(Box, { component: "button", type: "button", onClick: () => previewFile(file), sx: {
                                                                        width: 18,
                                                                        height: 18,
                                                                        border: "none",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: 12,
                                                                        background: "#FFFFFF",
                                                                        cursor: "pointer",
                                                                        padding: 0,
                                                                    }, "aria-label": "Preview file", children: _jsx("img", { src: "/assets/eye.svg", alt: "" }) })] }))] }), isUploading ? (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Box, { sx: {
                                                                flex: 1,
                                                                height: 6,
                                                                borderRadius: "999px",
                                                                backgroundColor: "#F3F4F6",
                                                                overflow: "hidden",
                                                            }, children: _jsx(Box, { sx: {
                                                                    width: `${progress}%`,
                                                                    height: "100%",
                                                                    backgroundColor: "#FF008E",
                                                                } }) }), _jsxs(Typography, { sx: { fontSize: 11, color: "#6B7280", minWidth: 40, textAlign: "right" }, children: [progress, "%"] })] })) : (_jsx(Typography, { sx: { fontSize: 11, fontWeight: 500, color: "#16A34A", ml: 5.5 }, children: "Completed" }))] }, key));
                                    }) }))] })] }) }), _jsxs(Dialog, { open: previewOpen, onClose: () => {
                    setPreviewOpen(false);
                    setPreviewUrl(null);
                    setPreviewName("");
                    setPreviewMime("");
                }, maxWidth: "md", fullWidth: true, children: [_jsxs(DialogTitle, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }, children: [_jsx(Typography, { sx: { fontSize: 14, fontWeight: 600, color: "#111827" }, children: previewName || "Preview" }), _jsx(IconButton, { size: "small", onClick: () => {
                                    setPreviewOpen(false);
                                    setPreviewUrl(null);
                                    setPreviewName("");
                                    setPreviewMime("");
                                }, "aria-label": "Close preview", children: "\u00D7" })] }), _jsx(DialogContent, { dividers: true, sx: { p: 0, backgroundColor: "#111827" }, children: previewUrl ? ((() => {
                            const mime = previewMime.toLowerCase();
                            const isPdf = mime.includes("pdf") || previewUrl.startsWith("data:application/pdf");
                            const isImage = mime.startsWith("image/") ||
                                previewUrl.startsWith("data:image/") ||
                                /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(previewUrl);
                            if (isPdf) {
                                return (_jsx(Box, { component: "iframe", src: previewUrl, title: previewName || "PDF Preview", sx: { width: "100%", height: "70vh", border: "none", backgroundColor: "#FFFFFF" } }));
                            }
                            if (isImage) {
                                return (_jsx(Box, { component: "img", src: previewUrl, alt: previewName || "Preview", sx: {
                                        width: "100%",
                                        height: "auto",
                                        maxHeight: "70vh",
                                        objectFit: "contain",
                                        display: "block",
                                        margin: "0 auto",
                                        backgroundColor: "#111827",
                                    } }));
                            }
                            // Fallback: open in new tab
                            return (_jsxs(Box, { sx: { p: 3, color: "#FFFFFF" }, children: [_jsx(Typography, { sx: { fontSize: 14, mb: 1 }, children: "Preview not available for this file type." }), _jsx(Box, { component: "button", type: "button", onClick: () => window.open(previewUrl, "_blank", "noopener,noreferrer"), style: {
                                            border: "none",
                                            background: "#FF008E",
                                            color: "#fff",
                                            padding: "10px 14px",
                                            borderRadius: 8,
                                            cursor: "pointer",
                                            fontWeight: 600,
                                        }, children: "Open in new tab" })] }));
                        })()) : (_jsx(Box, { sx: { p: 3, color: "#FFFFFF" }, children: _jsx(Typography, { sx: { fontSize: 14 }, children: "No preview available." }) })) })] }), _jsx(ConfirmActionModal, { open: deleteModal.open, title: "Remove File", description: "Are you sure you want to remove this file? This change will be saved when you save the ad request.", onConfirm: handleRemoveExistingFile, onClose: () => setDeleteModal({ open: false, key: null, file: null }), confirmText: "Remove", isDeleteIcon: true })] }));
};
export default CreateAdRequestDrawer;
